import type { Prompt } from '@meridian-ssa/shared';

import {
  ConfigSelector,
  type ModelConfig,
} from '@/components/shared/ConfigSelector';
import { Button } from '@/components/ui/Button';

interface SelectionStepProps {
  prompts: Prompt[];
  configs: ModelConfig[];
  selectedPrompts: string[];
  selectedConfigs: string[];
  selectedPromptSetId: string | null;
  onTogglePrompt: (id: string) => void;
  onConfigSelectionChange: (configIds: string[]) => void;
  onShowCSVImporter: () => void;
  onShowPromptSetManager: () => void;
  onNext: () => void;
  onCancel: () => void;
  multiplicity: number;
}

/** Get display name for a prompt, falling back to truncated text if name is missing */
function getPromptDisplayName(prompt: Prompt): string {
  if (prompt.name) return prompt.name;
  if (prompt.text) {
    const truncated = prompt.text.slice(0, 60);
    return truncated.length < prompt.text.length
      ? `${truncated}...`
      : truncated;
  }
  return 'Unnamed Prompt';
}

/** Get subtitle for a prompt (category/difficulty), or truncated text if no metadata */
function getPromptSubtitle(prompt: Prompt): string | null {
  const parts: string[] = [];
  if (prompt.category) parts.push(prompt.category);
  if (prompt.difficulty) parts.push(prompt.difficulty);

  if (parts.length > 0) return parts.join(' • ');

  // If no metadata and we have a name, show truncated text as subtitle
  if (prompt.name && prompt.text) {
    const truncated = prompt.text.slice(0, 80);
    return truncated.length < prompt.text.length
      ? `${truncated}...`
      : truncated;
  }

  return null;
}

export function SelectionStep({
  prompts,
  configs,
  selectedPrompts,
  selectedConfigs,
  selectedPromptSetId,
  onTogglePrompt,
  onConfigSelectionChange,
  onShowCSVImporter,
  onShowPromptSetManager,
  onNext,
  onCancel,
  multiplicity,
}: SelectionStepProps) {
  const totalExecutions =
    selectedPrompts.length * selectedConfigs.length * multiplicity;

  return (
    <>
      {/* Prompt Selection */}
      <div className="mb-5">
        <div className="flex justify-between items-center mb-2">
          <h3
            className="text-sm font-semibold m-0"
            style={{ color: '#1A1A1A' }}
          >
            Select Prompts ({selectedPrompts.length} selected)
          </h3>
          <div className="flex gap-1.5">
            <Button
              variant="secondary"
              size="small"
              onClick={onShowPromptSetManager}
            >
              Use Prompt Set
            </Button>
            <Button
              variant="secondary"
              size="small"
              onClick={onShowCSVImporter}
            >
              Import CSV
            </Button>
          </div>
        </div>

        {selectedPromptSetId && (
          <div
            className="px-2 py-1.5 rounded mb-2 text-xs"
            style={{ background: 'rgba(139, 35, 50, 0.08)', color: '#8B2332' }}
          >
            Using prompt set • {selectedPrompts.length} prompts selected
          </div>
        )}

        <div className="grid gap-1.5 max-h-56 overflow-y-auto pr-1">
          {prompts.map(prompt => {
            const isSelected = selectedPrompts.includes(prompt.id);
            return (
              <label
                key={prompt.id}
                className="flex items-center gap-2 px-2.5 py-2 rounded cursor-pointer transition-all duration-150"
                style={{
                  border: `1.5px solid ${isSelected ? '#8B2332' : '#E8E0D8'}`,
                  background: isSelected ? 'rgba(139, 35, 50, 0.05)' : 'white',
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onTogglePrompt(prompt.id)}
                  className="w-3.5 h-3.5 cursor-pointer"
                  style={{ accentColor: '#8B2332' }}
                />
                <div className="flex-1 min-w-0">
                  <div
                    className="text-sm font-medium truncate"
                    style={{ color: '#1A1A1A' }}
                  >
                    {getPromptDisplayName(prompt)}
                  </div>
                  {getPromptSubtitle(prompt) && (
                    <div
                      className="text-xs truncate"
                      style={{ color: '#6B6B6B' }}
                    >
                      {getPromptSubtitle(prompt)}
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Config Selection */}
      <div className="mb-1">
        <ConfigSelector
          configs={configs}
          selectedConfigIds={selectedConfigs}
          onSelectionChange={onConfigSelectionChange}
          defaultFilter="enabled"
          showFilterButtons={true}
        />
      </div>

      {/* Summary */}
      {totalExecutions > 0 && (
        <div
          className="px-3 py-2.5 rounded mb-4"
          style={{
            background: 'rgba(139, 35, 50, 0.08)',
            border: '1px solid rgba(139, 35, 50, 0.2)',
          }}
        >
          <div className="text-sm font-medium" style={{ color: '#1A1A1A' }}>
            Total: {totalExecutions} executions
          </div>
          <div className="text-xs mt-0.5" style={{ color: '#6B6B6B' }}>
            {selectedPrompts.length} prompts × {selectedConfigs.length} configs
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 justify-end">
        <Button variant="secondary" size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="small"
          onClick={onNext}
          disabled={totalExecutions === 0}
        >
          Next →
        </Button>
      </div>
    </>
  );
}
